import React, { useEffect, useState } from 'react';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../common';
import ResponseTable from './ResponseTable.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteFilled,
  EditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useDeletePollResponsesMutation,
  useGetPollResponsesQuery,
  useSetResponseCollectionActiveMutation,
} from '../../../../../redux/services/polls';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { IconButton } from '../../../../common/controls/Controls.jsx';

const ResponseCollectionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t('Kolekcie odpovedí pre anketu') + ' - FRI CMS';
  }, []); // Runs on mount

  const {
    deleteSuccess: showDeleteSuccess,
    saveSuccess: showSaveSuccess,
    saveFailed,
  } = useToastMessages();

  const [setCollectionActive] = useSetResponseCollectionActiveMutation();

  const toggleActive = async (row) => {
    try {
      await setCollectionActive({
        id: row.id,
        isActive: row.is_active === 0,
      }).unwrap();
      showSaveSuccess();
      refetch();
    } catch (err) {
      saveFailed(err);
    }
  };

  const { data, isLoading, refetch } = useGetPollResponsesQuery();

  const [deletePollResponse, { isSuccess: isDeleteSuccess }] =
    useDeletePollResponsesMutation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (isDeleteSuccess) {
      showDeleteSuccess();
      setSelectedId(null);
      setShowDeleteModal(false);
      refetch();
    }
  }, [isDeleteSuccess]);

  const handleOpenModal = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedId) {
      deletePollResponse(selectedId);
    }
  };

  const customSettings = (row) => (
    <div style={{ display: 'flex' }}>
      <IconButton
        icon={<EditOutlined />}
        tooltip={t('Upraviť')}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`${routes.cmsResponseCollectionsEdit}/${row.id}`);
        }}
      />
      <IconButton
        icon={row.is_active === 0 ? <CheckOutlined /> : <CloseOutlined />}
        tooltip={
          row.is_active === 0
            ? t('Aktivovať')
            : t('Deaktivovať (kolekcia sa nebude ponúkať pri vytváraní otázok)')
        }
        onClick={(e) => {
          e.stopPropagation();
          toggleActive(row);
        }}
      />
      <IconButton
        icon={
          <DeleteFilled
            style={{
              color: row.questions.length > 0 ? 'gray' : '',
            }}
          />
        }
        tooltip={t('Vymazať')}
        disabledTooltip={t('Kolekcia je už použítá v ankete.')}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedId(row.id);
          handleOpenModal();
        }}
        disabled={row.questions.length > 0}
      />
    </div>
  );

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Kolekcie odpovedí pre anketu')}
        backUrl={routes.cmsPolls}
        backUrlTooltip={t('Anketa')}
        createText={t('Pridať kolekciu odpovedí')}
        createUrl={routes.cmsResponseCollectionsCreate}
        permissionsBasic={'response-collections'}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <ResponseTable
            pollsResponseCollections={data}
            onDetailClick={(record) =>
              navigate(`${routes.cmsResponseCollectionsEdit}/${record.id}`)
            }
            customSettings={customSettings}
          />
        </CollapseBlock>
      </div>
      <QuestionModal
        visible={showDeleteModal}
        headerTitle={t('Potvrdenie vymazania')}
        question={t('Naozaj chcete vymazať túto kolekciu odpovedí?')}
        chosenPositive={handleDeleteConfirm}
        toggle={() => setShowDeleteModal(false)}
        yes={t('Áno')}
      />
    </>
  );
};

export default ResponseCollectionsPage;
